'use client';
import { createStyles, getSize, rem } from '@s7/styles';

function _extends() {
    _extends = Object.assign || function(target) {
        for(var i = 1; i < arguments.length; i++){
            var source = arguments[i];
            for(var key in source){
                if (Object.prototype.hasOwnProperty.call(source, key)) {
                    target[key] = source[key];
                }
            }
        }
        return target;
    };
    return _extends.apply(this, arguments);
}
function _object_without_properties_loose(source, excluded) {
    if (source == null) return {};
    var target = {};
    var sourceKeys = Object.keys(source);
    var key, i;
    for(i = 0; i < sourceKeys.length; i++){
        key = sourceKeys[i];
        if (excluded.indexOf(key) >= 0) continue;
        target[key] = source[key];
    }
    return target;
}
var heights = {
    xs1: rem(32),
    sm1: rem(40),
    md1: rem(48)
};
var paddings = {
    xs1: rem(8),
    sm1: rem(12),
    md1: rem(16)
};
var fontSizes = {
    xs1: rem(14),
    sm1: rem(14),
    md1: rem(16)
};
var lineHeights = {
    xs1: rem(20),
    sm1: rem(20),
    md1: rem(24)
};
var fontSizeStyles = function(size) {
    return {
        fontSize: getSize({
            size: size,
            sizes: fontSizes
        }),
        lineHeight: getSize({
            size: size,
            sizes: lineHeights
        })
    };
};
var placeholderStyles = function(theme) {
    return {
        opacity: 1,
        color: theme.palette.text.tertiary
    };
};
var fontStyles = function(theme, size) {
    var _fontSizeStyles = fontSizeStyles(size);
    return _extends({
        fontFamily: theme.typography.fontFamily
    }, _fontSizeStyles, {
        "&::placeholder": _extends({
            opacity: 1,
            color: theme.palette.text.tertiary
        }, _fontSizeStyles)
    });
};
var getInputShades = function(param, params) {
    var _param_fn = param.fn, deriveShades = _param_fn.deriveShades, themeColor = _param_fn.themeColor, palette = param.palette;
    var color = consolidateParams(params).color;
    var shades = deriveShades(themeColor(color, palette.primary));
    var baseShade = shades.base || shades[600];
    return {
        baseShade: baseShade,
        shades: shades
    };
};
var flexCenter = {
    display: "flex",
    alignItems: "center",
    justifyContent: "center"
};
var inputTweaks = {
    WebkitTapHighlightColor: "transparent",
    "&::-webkit-inner-spin-button, &::-webkit-outer-spin-button, &::-webkit-search-decoration, &::-webkit-search-cancel-button, &::-webkit-search-results-button, &::-webkit-search-results-decoration": {
        appearance: "none"
    },
    "&[type=number]": {
        MozAppearance: "textfield"
    }
};
var consolidateParams = function(_param) {
    var states = _param.states, state = _param.state, params = _object_without_properties_loose(_param, [
        "states",
        "state"
    ]);
    var stateParams = states == null ? void 0 : states[state];
    return _extends({}, params, stateParams, {
        state: state,
        states: states
    });
};
var useStyles = createStyles(function(theme, params, param) {
    var size = param.size;
    var _theme_fn = theme.fn, border = _theme_fn.border, focusRing = _theme_fn.focusRing;
    var _theme_palette = theme.palette, background = _theme_palette.background, borderColor = _theme_palette.border, text = _theme_palette.text;
    var _consolidateParams = consolidateParams(params), multiline = _consolidateParams.multiline, radius = _consolidateParams.radius, withBorder = _consolidateParams.withBorder, width = _consolidateParams.width;
    var padding = getSize({
        size: size,
        sizes: paddings
    });
    var height = multiline ? "auto" : getSize({
        size: size,
        sizes: heights
    });
    var borderRadius = radius ? rem(radius) : rem(8);
    var _fontStyles = fontStyles(theme, size);
    var _getInputShades = getInputShades(theme, params), baseShade = _getInputShades.baseShade, shades = _getInputShades.shades;
    return {
        wrapper: _extends({
            position: "relative",
            boxSizing: "content-box",
            backgroundColor: background.content2
        }, width && {
            width: width
        }, flexCenter, {
            borderRadius: borderRadius,
            height: height
        }, withBorder && border({
            color: borderColor.element
        }), withBorder && {
            "&:focus-within": _extends({}, border({
                color: baseShade
            }))
        }, {
            "&[data-disabled=true]": _extends({}, withBorder && border({
                color: borderColor.divider
            }), {
                backgroundColor: background.highlight,
                userSelect: "none",
                cursor: "not-allowed",
                "&::after": {
                    content: '""',
                    display: "block",
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%"
                }
            }),
            "&[data-state]": _extends({}, withBorder && border({
                color: baseShade
            }), focusRing({
                color: shades.mid || shades[200],
                selector: "&:focus-within"
            })),
            "&[data-autofill=true]": _extends({
                backgroundColor: theme.palette.info.min
            }, border({
                color: borderColor.element
            }))
        }),
        input: _extends({
            boxSizing: "border-box"
        }, inputTweaks, {
            color: text.primary,
            appearance: "none",
            resize: "none",
            outline: "none",
            border: "none",
            display: "block",
            overflow: "hidden",
            textOverflow: "ellipsis",
            textAlign: "left",
            background: "transparent",
            borderRadius: borderRadius,
            /* 
                  Огромный transitionDelay используется в целях скрытия браузерного background-color,
                  который никак иначе не перекрывается:
                  https://stackoverflow.com/questions/2781549/removing-input-background-colour-for-chrome-autocomplete
                 */ "&:autofill": {
                WebkitTextFillColor: "" + text.primary + " !important",
                transitionProperty: "background-color",
                transitionDelay: " 5000s"
            },
            "&:-webkit-autofill": {
                WebkitTextFillColor: "" + text.primary + " !important",
                transitionProperty: "background-color",
                transitionDelay: " 5000s",
                animationName: "onautofillstart"
            },
            "&:not(:-webkit-autofill)": {
                animationName: "onautofillcancel"
            }
        }, _fontStyles, {
            height: height,
            width: "100%",
            "&[data-left-section=false]": {
                paddingLeft: padding
            },
            "&[data-right-section=false]": {
                paddingRight: padding
            },
            "&:focus": {
                caretColor: baseShade
            },
            "&:disabled": {
                color: text.secondary,
                "&::placeholder": {
                    color: text.secondary
                }
            },
            "&::-webkit-search-cancel-button": {
                display: "none"
            }
        }),
        icon: _extends({}, flexCenter, {
            "&[data-right-side=true]": {
                marginRight: padding
            }
        }),
        section: _extends({
            zIndex: 1,
            height: "100%"
        }, _fontStyles, flexCenter, {
            paddingLeft: padding,
            paddingRight: padding
        }),
        leftSection: {},
        rightSection: {},
        side: _extends({}, flexCenter, {
            color: text.secondary,
            height: "100%"
        }),
        rightSide: {},
        leftSide: {},
        "@global": {
            "@keyframes onautofillstart": {},
            "@keyframes onautofillcancel": {}
        }
    };
});

export { useStyles as default, fontSizeStyles, fontStyles, getInputShades, placeholderStyles };
